import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';

import { QueryClientProvider } from '@tanstack/react-query';

import { routeTree } from './routeTree.gen';
import {
  pageViewTransition,
  quietSkippedTransitions,
} from './lib/pageTransition';
import { RequireAuth } from '@/lib/auth/RequireAuth';
import { SessionProvider } from '@/lib/auth/SessionProvider';
import { queryClient } from '@/lib/queryClient';
import { watchSystemTheme } from '@/shared/store/theme';
import '@/styles/globals.css';

watchSystemTheme();

// Before the router's first navigation: a skipped page transition is not an
// error, and must not reach the console or error reporting as one.
quietSkippedTransitions();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // Query owns caching; the router should not also cache loader results.
  defaultPreloadStaleTime: 0,
  // A short cross-fade between pages. Links that open one thing into its page
  // still morph through their own `viewTransition` (spec 075 FR-026).
  defaultViewTransition: pageViewTransition(),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

// Authentication is mounted here rather than in the root route, deliberately.
// `SessionProvider` starts an OIDC client, which fetches the provider's discovery
// document on mount — so putting it in the route tree would make *every* component
// test that renders a route reach for the network, and fail on an unmatched request.
// Tests render the router directly and therefore see the session context's
// signed-out default, which is what keeps them untouched (spec 067 SC-015).
//
// The query client is provided above the session because the session resolves the
// signed-in person through `/api/users/me`; the root route provides it again from
// router context, which is harmless and keeps tests self-contained.
createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <RequireAuth>
          <RouterProvider router={router} />
        </RequireAuth>
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
